import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Heart, Users, Shield, Target, Star } from 'lucide-react';

const AboutPage = () => {
  const { t } = useLanguage();

  const values = [
    { key: 'empathy', icon: Heart },
    { key: 'respect', icon: Users },
    { key: 'solidarity', icon: Shield },
    { key: 'autonomy', icon: Target },
    { key: 'commitment', icon: Star },
  ];

  const team = [
    {
      id: 'founder',
      name: 'Emmanuella Enat',
      role: t.about.founderTitle,
      image: 'https://customer-assets.emergentagent.com/job_hope-rebuild-2/artifacts/4wjzd88l_a93c4453-998b-4951-8ef7-0f6171838af8.jpg',
      isFounder: true,
    },
    {
      id: 'board-member-1',
      name: 'Arielle Léveillé',
      role: t.about.boardMemberTitle,
      image: null,
      isFounder: false,
    },
    {
      id: 'board-member-2',
      name: 'Nathalie Zamor',
      role: t.about.boardMemberTitle,
      image: 'https://customer-assets.emergentagent.com/job_hope-rebuild-2/artifacts/j7e6tioi_PHOTO-2026-03-26-15-13-31.jpg',
      isFounder: false,
    },
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="section-label">{t.mission.subtitle}</span>
            <h1 
              data-testid="about-title"
              className="font-serif text-4xl sm:text-5xl font-bold text-[#1A252C] mt-4 mb-6"
            >
              {t.about.pageTitle}
            </h1>
            <p className="text-lg text-[#546A76] leading-relaxed">
              {t.about.historyText}
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Mission */}
            <div data-testid="mission-block" className="animate-fade-in-up">
              <span className="section-label">{t.mission.subtitle}</span>
              <h2 className="font-serif text-3xl font-bold text-[#1A252C] mt-4 mb-6">
                {t.mission.title}
              </h2>
              <p className="text-[#546A76] leading-relaxed mb-4">
                {t.mission.text}
              </p>
              <p className="text-[#546A76] leading-relaxed">
                {t.mission.text2}
              </p>
            </div>

            {/* Vision */}
            <div data-testid="vision-block" className="animate-fade-in-up delay-200">
              <span className="section-label">{t.vision.subtitle}</span>
              <h2 className="font-serif text-3xl font-bold text-[#1A252C] mt-4 mb-6">
                {t.vision.title}
              </h2>
              <p className="text-[#546A76] leading-relaxed mb-4">
                {t.vision.text}
              </p>
              <p className="text-[#546A76] leading-relaxed">
                {t.vision.text2}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A252C]">
              {t.about.valuesTitle}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {values.map(({ key, icon: Icon }, index) => (
              <div
                key={key}
                data-testid={`value-${key}`}
                className="bg-white p-8 rounded-3xl text-center card-hover soft-shadow"
              >
                <div className="w-16 h-16 mx-auto rounded-2xl bg-[#4A708B]/10 flex items-center justify-center mb-6">
                  <Icon className="w-8 h-8 text-[#4A708B]" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#1A252C] mb-3">
                  {t.about.values[key].title}
                </h3>
                <p className="text-[#546A76] text-sm leading-relaxed">
                  {t.about.values[key].desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section data-testid="team-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A252C]">
              {t.about.teamTitle}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-12 max-w-4xl mx-auto">
            {team.map((member) => (
              <div
                key={member.id}
                data-testid={`team-member-${member.id}`}
                className="text-center"
              >
                <div className="relative mb-6">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-48 h-48 mx-auto rounded-full object-cover border-4 border-[#E2E8F0]"
                    />
                  ) : (
                    <div className="w-48 h-48 mx-auto rounded-full bg-[#4A708B]/10 border-4 border-[#E2E8F0] flex items-center justify-center">
                      <span className="font-serif text-4xl font-bold text-[#4A708B]">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                  )}
                  {member.isFounder && (
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-[#D4AF37] text-white text-xs font-semibold px-4 py-1 rounded-full">
                      {t.about.founderTitle}
                    </div>
                  )}
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#1A252C] mb-1">
                  {member.name}
                </h3>
                <p className="text-[#546A76]">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Section */}
      <section className="py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1637072103875-1b29a09d9c91?w=1200"
              alt="Femmes réunies en soutien mutuel — Fondation Enat Espoir & Protection, Québec"
              loading="lazy"
              className="w-full h-[450px] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A252C]/80 via-[#1A252C]/50 to-transparent flex items-center">
              <div className="max-w-xl px-8 sm:px-12">
                <h3 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-4">
                  {t.about.historyTitle}
                </h3>
                <p className="text-white/90 leading-relaxed">
                  {t.about.historyText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
